import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/Skeleton.tsx';
import { Card } from '@/components/ui/Card.tsx';
import { tableCardHeaderClass } from './paymentTableLayout';

const SeparatorSkeleton = ({
  className,
}: Pick<React.HTMLAttributes<HTMLDivElement>, 'className'>) => (
  <div className={cn('flex items-center gap-3 px-1 py-0.5', className)}>
    <Skeleton className="h-3 w-28" />
    <span aria-hidden className="h-px flex-1 bg-neutral-200" />
  </div>
);

const PersonTileSkeleton = () => (
  <Card className="flex items-center gap-3.5 overflow-hidden px-7 py-4">
    <Skeleton className="size-3.5 rounded-full" />
    <Skeleton className="size-10 rounded-full" />
    <div className="flex flex-col gap-1.5">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-3 w-28" />
    </div>
    <div className="ml-auto flex items-center gap-4">
      <div className="flex flex-col items-end gap-1.5">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-3 w-20" />
      </div>
      <Skeleton className="h-8 w-24 rounded-[7px]" />
    </div>
  </Card>
);

const PaymentTableSkeleton = ({ rowCount }: { rowCount: number }) => (
  <Card className="@container overflow-hidden">
    <div className={tableCardHeaderClass}>
      <Skeleton className="h-3 w-36" />
      <Skeleton className="h-3 w-24" />
    </div>
    <div className="flex flex-col">
      {Array.from({ length: rowCount }, (_, index) => (
        <div
          key={index}
          className="@min-[600px]:px-7 flex items-center justify-between gap-5 border-b border-dashed border-neutral-100 px-4 py-3.5"
        >
          <Skeleton className="h-3.5 w-32" />
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-6 w-28 rounded-md" />
        </div>
      ))}
    </div>
    <div className="@min-[600px]:px-7 flex items-baseline justify-between px-4 pb-[18px] pt-3.5">
      <Skeleton className="h-3.5 w-24" />
      <Skeleton className="h-3.5 w-28" />
    </div>
  </Card>
);

/** Nagłówek „Płatności” z pigułką tytułu przelewu — układ jak w `BookingBillView`. */
const PaymentsHeaderSkeleton = () => (
  <div className="flex flex-col gap-2 pt-3.5">
    <SeparatorSkeleton />
    <Skeleton className="h-8 w-full rounded-full" />
  </div>
);

/** Szkielet ekranu rezerwacji — kafle osób, wartość, tytuł przelewu i harmonogram płatności. */
const BookingBillViewSkeleton = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-3.5', className)} {...props}>
    <SeparatorSkeleton />
    <PersonTileSkeleton />

    <SeparatorSkeleton className="pt-3.5" />
    <PersonTileSkeleton />
    <PersonTileSkeleton />

    <Card className="flex items-baseline justify-between gap-5 overflow-hidden px-7 py-4">
      <Skeleton className="h-4 w-44" />
      <Skeleton className="h-6 w-32" />
    </Card>

    <PaymentsHeaderSkeleton />
    <PaymentTableSkeleton rowCount={3} />
    <PaymentTableSkeleton rowCount={2} />
  </div>
);

export default BookingBillViewSkeleton;
